// DetalleNotasCredito.jsx
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, RefreshCw, Filter, X, FilterX, Tag, ChevronDown,
  Calendar, Database, User, Package, Building2, List,
  DollarSign, Download, ChevronRight, Boxes, Banknote,
  FileX, RotateCcw, Users, 
} from 'lucide-react';
import DashboardMenuHeader from '../components/FacturacionMenuHeader';

// ⚠️ Ajusta este import al header de la sección donde viva esta vista
// import VentasMenuHeader from '../components/VentasMenuHeader';
// import CobranzaMenuHeader from '../components/CobranzaMenuHeader';

import FactLoader from './FactLoader';
import { useQuery } from '../QueryContext';
import { useAuth } from '../AuthContext';
import './DashboardBultos.css';
import * as XLSX from 'xlsx';

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

const PAGE_SIZE = 500;

const filterLabels = {
  TIPO_NC: "Tipo NC",
  ANULADA: "Anulada",
  BI_PROVEEDOR: "Proveedor",
  BI_MARCA: "Marca",
  BI_LINEA: "Línea",
  BI_SEGMENTO: "Segmento BI",
  BI_CLASIF2: "Clasificación",
  BI_ASP: "ASP",
  CLI_PRIN: "Cliente Principal",
  CLI_VEN_DES: "Vendedor",
  CLI_REG_VERD: "Región",
  CLI_CANAL: "Canal",
  CLI_CANAL_REAL: "Canal Real",
  CLI_SEG_DES: "Segmento Cliente",
  CLI_TERRITORIO: "Territorio",
  CLI_ID_TIPO: "Tipo Cliente",
  ESPECIALISTA: "Especialista",
  USER_ESP: "Especialista User",
};

const INITIAL_FILTERS = {
  TIPO_NC: [],
  ANULADA: ['NO'],   // por defecto solo vigentes
  BI_PROVEEDOR: [],
  BI_MARCA: [],
  BI_LINEA: [],
  BI_SEGMENTO: [],
  BI_CLASIF2: [],
  BI_ASP: [],
  CLI_PRIN: [],
  CLI_VEN_DES: [],
  CLI_REG_VERD: [],
  CLI_CANAL: [],
  CLI_CANAL_REAL: [],
  CLI_SEG_DES: [],
  CLI_TERRITORIO: [],
  CLI_ID_TIPO: [],
  ESPECIALISTA: [],
  USER_ESP: [],
};

const formatUSD = (num) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num || 0);
const formatBs = (num) =>
  new Intl.NumberFormat('es-VE', { style: 'currency', currency: 'VES' }).format(num || 0);
const formatNum = (num) =>
  new Intl.NumberFormat('de-DE').format(num || 0);

// ─── MultiSelectFilter (mismo componente que DetalleFact) ──────────────
const MultiSelectFilter = ({ label, name, icon: Icon, options = [], selectedValues = [], onChange, displayMap }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = options
    .filter((opt) => String(opt).toLowerCase().includes(searchTerm.toLowerCase()))
    .slice(0, 100);

  const getDisplay = (v) => (displayMap && displayMap[v]) || v;

  return (
    <div className="filter-pill-container" ref={dropdownRef}>
      <button
        className={`filter-pill-button ${selectedValues.length > 0 ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Icon size={12} className="pill-icon" />
        <span className="pill-label">{label}:</span>
        <span className="pill-value">
          {selectedValues.length > 0
            ? (selectedValues.length === 1
                ? getDisplay(selectedValues[0])
                : `${selectedValues.length} Selec.`)
            : 'Todos'}
        </span>
        <ChevronDown size={11} className={`pill-arrow ${isOpen ? 'up' : ''}`} />
      </button>

      {isOpen && (
        <div className="filter-dropdown-panel">
          <div className="search-wrapper">
            <Search size={14} className="search-icon" />
            <input
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>
          <div className="options-list">
            {filteredOptions.map((opt, i) => (
              <label key={i} className="checkbox-row">
                <input
                  type="checkbox"
                  checked={selectedValues.includes(opt)}
                  onChange={() => {
                    const newVals = selectedValues.includes(opt)
                      ? selectedValues.filter((v) => v !== opt)
                      : [...selectedValues, opt];
                    onChange(name, newVals);
                  }}
                />
                <span>{getDisplay(opt)}</span>
              </label>
            ))}
          </div>
          {selectedValues.length > 0 && (
            <button className="reset-pill-link" onClick={() => onChange(name, [])}>
              Limpiar
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Componente principal ──────────────────────────────────────────────
const DetalleNotasCredito = () => {
  const { ncQueryDetalle, getNotasCreditoFilters, ncExportar, loading, error } = useQuery();
  const { session } = useAuth();

  const [isQuerying, setIsQuerying] = useState(false);
  const [rows, setRows] = useState([]);
  const [config, setConfig] = useState(null);
  const [availableOptions, setAvailableOptions] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [dynamicYears, setDynamicYears] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const filtersInitialized = useRef(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [globalTotals, setGlobalTotals] = useState({
    total_docs: 0, total_clientes: 0, total_cantidad: 0,
    total_bs: 0, total_usd: 0, count_dev: 0, count_fin: 0,
  });

  const [searchValue, setSearchValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  const searchRef = useRef(null);
  const tableContainerRef = useRef(null);

  // Fecha por defecto: mes + año actual
  const today = useMemo(() => new Date(), []);
  const nombreMesActual = useMemo(
    () => today.toLocaleString('es-ES', { month: 'long' }).toUpperCase(),
    [today]
  );

  const [selectedYear, setSelectedYear] = useState([today.getFullYear()]);
  const [selectedMonth, setSelectedMonth] = useState([nombreMesActual]);
  const [selectedDay, setSelectedDay] = useState([]);

  const meses = useMemo(
    () => ["ENERO","FEBRERO","MARZO","ABRIL","MAYO","JUNIO","JULIO","AGOSTO","SEPTIEMBRE","OCTUBRE","NOVIEMBRE","DICIEMBRE"],
    []
  );
  const days = useMemo(() => Array.from({ length: 31 }, (_, i) => i + 1), []);

  // ─── Etiquetas visibles de filtros ─────────────────────────────
  const displayMaps = {
    TIPO_NC: { DEVOLUCION: 'Devolución', FINANCIERA: 'Financiera' },
    ANULADA: { SI: 'Sí', NO: 'No' },
  };

  // ─── Totales de la página actual (útil como contador) ─────────
  const pageTotalUSD = useMemo(
    () => rows.reduce((acc, r) => acc + Number(r.NETO_USD || 0), 0),
    [rows]
  );
  const pageTotalBs = useMemo(
    () => rows.reduce((acc, r) => acc + Number(r.NETO || 0), 0),
    [rows]
  );

  // ─── Agrupación por NRO_DOC ────────────────────────────────────
  const groupedRows = useMemo(() => {
    const grouped = {};
    rows.forEach((row) => {
      const doc = row.NRO_DOC || 'SIN_DOC';
      if (!grouped[doc]) {
        grouped[doc] = {
          doc,
          tipo: row.TIPO_NC,
          fecha: row.FECHA,
          fechaVenc: row.FEC_VENC,
          clienteId: row.CLIENTE_ID,
          cliente: row.CLI_PRIN || row.CLI_NOMBRE || 'DESCONOCIDO',
          vendedor: row.CLI_VEN_DES || row.USERNAME || 'SIN ASESOR',
          especialista: row.ESPECIALISTA || '',
          region: row.CLI_REG_VERD || 'SIN REGIÓN',
          canal: row.CLI_CANAL || row.CLI_CANAL_REAL || 'SIN CANAL',
          motivo: row.MOTIVO_CONCEPTO || '',
          observacion: row.OBSERVACION || '',
          anulada: String(row.ANULADA || '').toUpperCase(),
          nroFacturaOrig: row.NRO_FACT_ORIG || row.NRO_NCR || '',
          totalCantidad: 0,
          totalBs: 0,
          totalUSD: 0,
          items: [],
        };
      }
      grouped[doc].items.push(row);
      grouped[doc].totalCantidad += Number(row.CANTIDAD || 0);
      grouped[doc].totalBs += Number(row.NETO || 0);
      grouped[doc].totalUSD += Number(row.NETO_USD || 0);
    });
    return Object.values(grouped);
  }, [rows]);

  const toggleDoc = (doc) => {
    setExpandedRows((prev) => ({ ...prev, [doc]: !prev[doc] }));
  };

  // ─── Highlight de búsqueda ────────────────────────────────────
  const highlightMatch = (text, query) => {
    if (!query || typeof text !== 'string') return text;
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return (
      <>
        {parts.map((part, i) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <strong key={i} style={{ color: '#00c6ff' }}>{part}</strong>
          ) : (
            part
          )
        )}
      </>
    );
  };

  // ─── Click fuera del search ──────────────────────────────────
  useEffect(() => {
    const handleClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // ─── Sugerencias dinámicas ────────────────────────────────────
  useEffect(() => {
    if (searchValue.length < 2) {
      setSuggestions([]);
      return;
    }
    const term = searchValue.toLowerCase();
    const results = [];
    const categories = [
      { key: 'CLI_PRIN', label: 'Cliente', icon: <Building2 size={14} /> },
      { key: 'BI_MARCA', label: 'Marca', icon: <Tag size={14} /> },
      { key: 'BI_PROVEEDOR', label: 'Proveedor', icon: <Package size={14} /> },
      { key: 'CLI_VEN_DES', label: 'Vendedor', icon: <User size={14} /> },
    ];
    categories.forEach((cat) => {
      const opts = availableOptions[cat.key] || config?.availableOptions?.[cat.key] || [];
      const matches = opts
        .filter((opt) => String(opt).toLowerCase().includes(term))
        .slice(0, 5)
        .map((val) => ({
          key: cat.key,
          value: val,
          category: cat.key,
          catLabel: cat.label,
          icon: cat.icon,
        }));
      results.push(...matches);
    });
    setSuggestions(results);
    setShowSuggestions(true);
  }, [searchValue, availableOptions, config]);

  // ─── Query params ─────────────────────────────────────────────
  const buildQueryParams = useCallback((pageNum = 1) => ({
    page_anios: selectedYear.map(String),
    page_meses: selectedMonth,
    page_dias: selectedDay.map(Number),
    tipo_nc: filters.TIPO_NC.length === 1 ? filters.TIPO_NC[0] : null,
    filters: {
      ...filters,
      TIPO_NC: filters.TIPO_NC.length > 1 ? filters.TIPO_NC : undefined,
    },
    page: pageNum,
    page_size: PAGE_SIZE,
    user_info: session?.user,
  }), [selectedYear, selectedMonth, selectedDay, filters, session]);

  // ─── Ejecutar query ───────────────────────────────────────────
  const executeQuery = useCallback(async (params) => {
    setIsQuerying(true);
    try {
      const result = await ncQueryDetalle(params);

      const tableData = result?.rows || result?.data || [];
      const totals = result?.totals || {};

      if (Array.isArray(tableData)) {
        setRows(tableData);
        setTotalCount(Number(result?.total || tableData.length));
        setTotalPages(Number(result?.total_pages || Math.ceil(tableData.length / PAGE_SIZE) || 1));

        setGlobalTotals({
          total_docs: Number(totals.total_docs || 0),
          total_clientes: Number(totals.total_clientes || 0),
          total_cantidad: Number(totals.total_cantidad || 0),
          total_bs: Number(totals.total_bs || 0),
          total_usd: Number(totals.total_usd || 0),
          count_dev: Number(totals.count_dev || 0),
          count_fin: Number(totals.count_fin || 0),
        });

        if (tableContainerRef.current) tableContainerRef.current.scrollTop = 0;
      }
    } catch (e) {
      console.error('NC query error:', e);
    } finally {
      setIsQuerying(false);
    }
  }, [ncQueryDetalle]);

  const handleApplyFilters = useCallback((page = 1) => {
    setCurrentPage(page);
    setShowSuggestions(false);
  }, []);

  // ─── Cargar config al montar ──────────────────────────────────
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const conf = await getNotasCreditoFilters();
        if (conf) {
          setConfig(conf);
          setDynamicYears(conf.availableYears || []);
          setAvailableOptions(conf.availableOptions || {});

          // Si el año actual no está en data, usar el más reciente
          if (conf.availableYears?.length && !conf.availableYears.includes(today.getFullYear())) {
            setSelectedYear([conf.availableYears[0]]);
          }
        }
        filtersInitialized.current = true;
      } catch (e) {
        console.error('Config load error:', e);
      }
    };
    loadConfig();
  }, [getNotasCreditoFilters, today]);

  // ─── Ejecución central con debounce ──────────────────────────
  useEffect(() => {
    if (filtersInitialized.current) {
      const timer = setTimeout(async () => {
        const params = buildQueryParams(currentPage);
        await executeQuery(params);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [selectedYear, selectedMonth, selectedDay, filters, currentPage, buildQueryParams, executeQuery]);

  // ─── Resetear a página 1 cuando cambian filtros ─────────────
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedYear, selectedMonth, selectedDay, filters]);

  const selectSuggestion = (sug) => {
    setFilters((prev) => ({ ...prev, [sug.category]: [sug.value] }));
    setSelectedCategory(sug);
    setSearchValue('');
    setShowSuggestions(false);
    setCurrentPage(1);
  };

  // ─── Refresh ─────────────────────────────────────────────────
  const handleRefresh = useCallback(async () => {
    try {
      setIsQuerying(true);
      const conf = await getNotasCreditoFilters(true);
      if (conf) {
        setConfig(conf);
        setDynamicYears(conf.availableYears || []);
        setAvailableOptions(conf.availableOptions || {});
      }

      setFilters(INITIAL_FILTERS);
      setSearchValue('');
      setSelectedCategory(null);
      setSelectedYear([today.getFullYear()]);
      setSelectedMonth([nombreMesActual]);
      setSelectedDay([]);
      setCurrentPage(1);

      await executeQuery({
        page_anios: [String(today.getFullYear())],
        page_meses: [nombreMesActual],
        page_dias: [],
        tipo_nc: null,
        filters: INITIAL_FILTERS,
        page: 1,
        page_size: PAGE_SIZE,
        user_info: session?.user,
      });
    } catch (e) {
      console.error('Refresh error:', e);
    } finally {
      setIsQuerying(false);
    }
  }, [getNotasCreditoFilters, executeQuery, today, nombreMesActual, session]);

  // ─── Exportación Excel/CSV ──────────────────────────────────
  // 1. CSV completo (vía API, todos los filtros aplicados)
  const handleExportFullCSV = useCallback(async () => {
    try {
      setIsQuerying(true);
      const params = buildQueryParams(1);
      await ncExportar(params);
    } catch (e) {
      console.error('Export error:', e);
    } finally {
      setIsQuerying(false);
    }
  }, [buildQueryParams, ncExportar]);

  // 2. Excel con la página actual (client-side, rápido)
  const handleExportCurrentPage = useCallback(() => {
    if (!rows.length) return;
    const data = rows.map((r) => ({
      'Tipo NC': r.TIPO_NC,
      Año: r.MASTER_AÑO,
      Mes: r.MASTER_MES,
      Fecha: r.FECHA,
      Vencimiento: r.FEC_VENC,
      'Nro Doc': r.NRO_DOC,
      'Doc Origen': r.NRO_FACT_ORIG || r.NRO_NCR || '',
      'Cliente ID': r.CLIENTE_ID,
      'Cliente': r.CLI_PRIN || r.CLI_NOMBRE,
      'Vendedor': r.CLI_VEN_DES || r.USERNAME,
      'Especialista': r.ESPECIALISTA || r.USER_ESP || '',
      'Región': r.CLI_REG_VERD,
      'Canal': r.CLI_CANAL,
      'Proveedor': r.BI_PROVEEDOR,
      'Marca': r.BI_MARCA,
      'Línea': r.BI_LINEA,
      'Clasificación': r.BI_CLASIF2,
      'Art ID': r.ART_ID,
      'Descripción': r.BI_DESC1 || r.ART_DESC,
      'Cantidad': Number(r.CANTIDAD || 0),
      'UND': r.UND || '',
      'Neto Bs': Number(r.NETO || 0),
      'Neto USD': Number(r.NETO_USD || 0),
      'Tasa': Number(r.TASA || 0),
      'Motivo': r.MOTIVO_CONCEPTO || '',
      'Observación': r.OBSERVACION || '',
      'Anulada': r.ANULADA,
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "NotasCredito");
    XLSX.writeFile(
      wb,
      `NotasCredito_${new Date().toISOString().split('T')[0]}_pag${currentPage}.xlsx`
    );
  }, [rows, currentPage]);

  // ─── Badge de TIPO_NC ────────────────────────────────────────
  const renderTipoBadge = (tipo) => {
    const isDev = String(tipo).toUpperCase() === 'DEVOLUCION';
    return (
      <span
        style={{
          padding: '4px 10px',
          borderRadius: 8,
          fontSize: 11,
          fontWeight: 700,
          background: isDev ? '#f59e0b' : '#3b82f6',
          color: isDev ? 'white' : 'white',
          border: `1px solid ${isDev ? '#f59e0b' : '#3b82f6'}`,
        }}
      >
        {isDev ? 'DEVOLUCIÓN' : 'FINANCIERA'}
      </span>
    );
  };

  // ─── Badge de ANULADA ────────────────────────────────────────
  const renderEstadoBadge = (anulada) => {
    const isAnulada = String(anulada || '').toUpperCase() === 'SI';
    return (
      <span
        style={{
          padding: '4px 10px',
          borderRadius: 8,
          fontSize: 11,
          fontWeight: 700,
          background: isAnulada ? 'rgb(101, 0, 0)' : 'rgb(0, 64, 30)',
          color: isAnulada ? '#ff4d4f' : '#00d26a',
          border: isAnulada
            ? '1px solid rgba(255,77,79,0.35)'
            : '1px solid rgba(0,210,106,0.35)',
        }}
      >
        {isAnulada ? 'ANULADA' : 'VIGENTE'}
      </span>
    );
  };

  // ─── Columnas tabla principal (agrupada) ────────────────────
  const tableHeaders = [
    'Fecha', 'Documento', 'Tipo', 'Cliente', 'Vendedor',
    'Cant.', 'Neto Bs', 'Neto USD', 'Status', 'Motivo',
  ];

  // ─── Estados de carga / error ────────────────────────────────
  if (loading && !config) {
    return <FactLoader message="Cargando datos de Notas de Crédito..." />;
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#ff6b6b' }}>
        <h3>Error de conexión</h3>
        <p>{error}</p>
        <button onClick={handleRefresh} className="refresh-button" style={{ marginTop: '20px' }}>
          <RefreshCw size={20} /> Reintentar
        </button>
      </div>
    );
  }

  return (
    <motion.div className="dashboard-container" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      {/* <VentasMenuHeader /> */}

      <DashboardMenuHeader />

      <div style={{ marginTop: '20px' }}>
        <header className="header-flex">
          <div className="title-section">
            <h1 className="dashboard-title">Detalle de Notas de Crédito</h1>
            <div className="title-underline"></div>
          </div>

          <div className="date-selectors">
            <button className="refresh-button" onClick={handleRefresh} title="Reiniciar filtros">
              <RefreshCw size={16} className={loading || isQuerying ? "spin-anim" : ""} />
            </button>
            <button
              className="refresh-button"
              onClick={handleExportFullCSV}
              title="Exportar TODO (CSV)"
              style={{ background: '#00ba52' }}
              disabled={isQuerying}
            >
              <Download size={16} />
            </button>

            <div className="filters-and-search-group">
              <div className="date-filters-group">
                <MultiSelectFilter
                  label="Día"
                  name="day"
                  icon={Calendar}
                  options={days}
                  selectedValues={selectedDay}
                  onChange={(_, v) => setSelectedDay(v)}
                />
                <MultiSelectFilter
                  label="Mes"
                  name="month"
                  icon={Calendar}
                  options={meses}
                  selectedValues={selectedMonth}
                  onChange={(_, v) => setSelectedMonth(v)}
                />
                <MultiSelectFilter
                  label="Año"
                  name="year"
                  icon={Calendar}
                  options={isMobile ? [today.getFullYear()] : dynamicYears}
                  selectedValues={selectedYear}
                  onChange={(_, v) => setSelectedYear(v)}
                />
              </div>

              <div className="global-search-container" ref={searchRef}>
                <div className="global-search-input-wrapper">
                  <input
                    type="text"
                    placeholder="Buscar cliente, marca, proveedor..."
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters(1)}
                    className="global-search-input"
                  />
                  <button className="global-search-apply-btn" onClick={() => handleApplyFilters(1)}>
                    <Search size={18} />
                  </button>
                </div>

                <AnimatePresence>
                  {showSuggestions && suggestions.length > 0 && (
                    <motion.div
                      className="global-search-dropdown"
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -5 }}
                    >
                      <div className="suggestions-list">
                        {suggestions.map((opt, index) => (
                          <div
                            key={`${opt.key}-${index}`}
                            className={`suggestion-item ${selectedCategory?.value === opt.value && selectedCategory?.key === opt.key ? 'selected' : ''}`}
                            onClick={() => selectSuggestion(opt)}
                          >
                            <div className="suggestion-category">{opt.catLabel}:</div>
                            <div className="suggestion-value">
                              {highlightMatch(opt.value, searchValue)}
                              {filters[opt.category]?.includes(opt.value) && (
                                <span className="suggestion-active">[OK]</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            <button
              className="mobile-filter-btn"
              onClick={() => setShowFilters(true)}
              style={!isMobile ? { marginLeft: 'auto' } : {}}
            >
              <Filter size={16} /> <span>Filtros</span>
            </button>
          </div>
        </header>

        {/* Filtros laterales */}
        <div className={`filters-sidebar-container ${showFilters ? 'open' : ''}`}>
          <div className="filters-sidebar-header">
            <h3>Filtros Avanzados</h3>
            <button className="close-filters-btn" onClick={() => setShowFilters(false)}>
              <X size={20} />
            </button>
          </div>
          <div className="filters-grid">
            {Object.keys(filters).map((key) => (
              <MultiSelectFilter
                key={key}
                label={filterLabels[key]}
                name={key}
                icon={Tag}
                options={
                  key === 'TIPO_NC'
                    ? ['DEVOLUCION', 'FINANCIERA']
                    : key === 'ANULADA'
                    ? ['NO', 'SI']
                    : availableOptions[key] || []
                }
                selectedValues={filters[key]}
                onChange={(n, v) => setFilters((prev) => ({ ...prev, [n]: v }))}
                displayMap={displayMaps[key]}
              />
            ))}
            <button
              className="filter-pill-button reset-filters"
              onClick={() => setFilters(INITIAL_FILTERS)}
            >
              <FilterX size={12} /> Limpiar
            </button>
          </div>
        </div>

        {/* KPIs globales (filtro aplicado) */}
        <div className="dashboard-indicators3">
          <div className="indicator-card2 v-real">
            <div className="card-content">
              <div className="inven-icon-wrapper">
                <FileX size={22} color="white" />
              </div>
              <span className="indicator-label">Total Notas</span>
              <div className="indicator-right-group">
                <span className="indicator-value">{formatNum(globalTotals.total_docs)}</span>
              </div>
            </div>
          </div>

          <div className="indicator-card2 v-real">
            <div className="card-content">
              <div className="inven-icon-wrapper">
                <Users size={22} color="white" />
              </div>
              <span className="indicator-label">Clientes</span>
              <div className="indicator-right-group">
                <span className="indicator-value">{formatNum(globalTotals.total_clientes)}</span>
              </div>
            </div>
          </div>

          <div className="indicator-card2 v-real">
            <div className="card-content">
              <div className="inven-icon-wrapper">
                <Boxes size={22} color="white" />
              </div>
              <span className="indicator-label">Cantidad Total</span>
              <div className="indicator-right-group">
                <span className="indicator-value">{formatNum(globalTotals.total_cantidad)}</span>
              </div>
            </div>
          </div>

          <div className="indicator-card2 v-real">
            <div className="card-content">
              <div className="inven-icon-wrapper">
                <Banknote size={22} color="white" />
              </div>
              <span className="indicator-label">Total Bs.</span>
              <div className="indicator-right-group">
                <span className="indicator-value">{formatBs(globalTotals.total_bs)}</span>
              </div>
            </div>
          </div>

          <div className="indicator-card2 v-real">
            <div className="card-content">
              <div className="inven-icon-wrapper">
                <DollarSign size={22} color="white" />
              </div>
              <span className="indicator-label">Total USD</span>
              <div className="indicator-right-group">
                <span className="indicator-value">{formatUSD(globalTotals.total_usd)}</span>
              </div>
            </div>
          </div>

          <div className="indicator-card2 v-real">
            <div className="card-content">
              <div className="inven-icon-wrapper">
                <RotateCcw size={22} color="white" />
              </div>
              <span className="indicator-label">Dev / Fin</span>
              <div className="indicator-right-group">
                <span className="indicator-value" style={{ fontSize: '1rem' }}>
                  {formatNum(globalTotals.count_dev)} / {formatNum(globalTotals.count_fin)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabla principal */}
        <div className="detail-table-container">
          <div className="table-header">
            <h3>Documentos de Notas de Crédito</h3>
            <div className="table-controls">
              <button
                className="export-table-btn"
                onClick={handleExportCurrentPage}
                disabled={rows.length === 0}
                title="Exportar página actual (Excel)"
              >
                <Download size={18} />
              </button>

              <span>Total registros: {totalCount}</span>

              <div className="pagination-controls">
                <button
                  onClick={() => handleApplyFilters(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1 || isQuerying}
                >
                  Anterior
                </button>
                <span>
                  Página {currentPage} de {totalPages || 1}
                </span>
                <button
                  onClick={() => handleApplyFilters(currentPage + 1)}
                  disabled={currentPage >= (totalPages || 1) || isQuerying}
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>

          {rows.length === 0 ? (
            <div style={{ padding: '50px', textAlign: 'center', opacity: 0.5 }}>
              {isQuerying ? 'Cargando...' : 'No hay notas de crédito que coincidan con los filtros.'}
            </div>
          ) : (
            <>
              <div
                className="responsive-table-wrapper"
                ref={tableContainerRef}
                style={{ maxHeight: '62vh', overflow: 'auto' }}
              >
                <table className="detail-table">
                  <thead>
                    <tr>
                      {tableHeaders.map((header, index) => (
                        <th
                          key={index}
                          style={{
                            textAlign:
                              header.includes('Neto') || header.includes('Cant')
                                ? 'right'
                                : 'left',
                          }}
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {groupedRows.map((group, index) => {
                      const isExpanded = expandedRows[group.doc];
                      return (
                        <React.Fragment key={index}>
                          {/* FILA PRINCIPAL */}
                          <tr onClick={() => toggleDoc(group.doc)} style={{ opacity: 1 }}>
                            <td>{group.fecha}</td>

                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                <span>{group.doc}</span>
                              </div>
                              {group.nroFacturaOrig && (
                                <div style={{ fontSize: 11, opacity: 0.6 }}>
                                  Orig: {group.nroFacturaOrig}
                                </div>
                              )}
                            </td>

                            <td>{renderTipoBadge(group.tipo)}</td>

                            <td>
                              {group.cliente}
                              <div style={{ fontSize: 11, opacity: 0.7 }}>
                                ID: {group.clienteId}
                              </div>
                              <div style={{ fontSize: 11, opacity: 0.5 }}>
                                {group.region} · {group.canal}
                              </div>
                            </td>

                            <td>
                              {group.vendedor}
                              {group.especialista && (
                                <div style={{ fontSize: 11, opacity: 0.6 }}>
                                  Esp: {group.especialista}
                                </div>
                              )}
                            </td>

                            <td style={{ textAlign: 'right' }}>
                              {formatNum(group.totalCantidad)}
                            </td>

                            <td style={{ textAlign: 'right' }}>
                              {formatBs(group.totalBs)}
                            </td>

                            <td style={{ textAlign: 'right' }}>
                              {formatUSD(group.totalUSD)}
                            </td>

                            <td>{renderEstadoBadge(group.anulada)}</td>

                            <td style={{ fontSize: 12, maxWidth: 220 }}>
                              {group.motivo
                                ? group.motivo.length > 40
                                  ? group.motivo.substring(0, 40) + '...'
                                  : group.motivo
                                : '—'}
                            </td>
                          </tr>

                          {/* DETALLE EXPANDIDO */}
                          {isExpanded &&
                            group.items.map((item, subIndex) => (
                              <tr
                                key={`${index}-${subIndex}`}
                                style={{ background: 'rgba(255,255,255,0.02)' }}
                              >
                                <td></td>
                                <td style={{ fontSize: 11, opacity: 0.7 }}>
                                  {item.ART_ID}
                                </td>
                                <td style={{ fontSize: 11 }}>
                                  {item.BI_MARCA || '—'}
                                </td>
                                <td
                                  style={{
                                    fontSize: 12,
                                    maxWidth: 320,
                                  }}
                                >
                                  {item.BI_DESC1 || item.ART_DESC}
                                  <div style={{ fontSize: 10, opacity: 0.6 }}>
                                    {item.BI_PROVEEDOR} · {item.BI_LINEA}
                                  </div>
                                </td>
                                <td style={{ fontSize: 11, opacity: 0.7 }}>
                                  {item.USERNAME || '—'}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {formatNum(item.CANTIDAD)}
                                  <div style={{ fontSize: 10, opacity: 0.6 }}>
                                    {item.UND || ''}
                                  </div>
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {formatBs(item.NETO)}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {formatUSD(item.NETO_USD)}
                                </td>
                                <td style={{ fontSize: 11 }}>
                                  {item.ANULADA}
                                </td>
                                <td style={{ fontSize: 11, maxWidth: 220 }}>
                                  {item.MOTIVO_CONCEPTO || item.OBSERVACION || '—'}
                                </td>
                              </tr>
                            ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div
                className="table-footer-pagination"
                style={{
                  display: 'flex',
                  bottom: 0,
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '15px',
                  background: 'rgba(0,0,0,0.2)',
                  borderRadius: '0 0 12px 12px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-third)' }}>
                    <List size={16} />
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-third)' }}>
                      <strong>{formatNum(totalCount)}</strong> registros
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: '20px' }}>
                    <DollarSign size={16} color="#00ff88" />
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-third)' }}>
                      Página USD: <strong>{formatUSD(pageTotalUSD)}</strong>
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: '20px' }}>
                    <Banknote size={16} color="#ffd700" />
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-third)' }}>
                      Página Bs: <strong>{formatBs(pageTotalBs)}</strong>
                    </span>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: 0.6, color: 'var(--text-third)' }}>
                  <Database size={14} />
                  <span style={{ fontSize: '0.7rem', color: 'var(--text-third)' }}>DimassiDB · Notas de Crédito</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default DetalleNotasCredito;